import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PageTitle, useRouteModulePermissions } from "@/components/common/Crud";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Hash, Building2, Trophy, Award } from "lucide-react";

function StudentPicker({ students, selected, setSelected }) {
  const allOn =
    students.length && students.every((s) => selected.includes(s.id));
  const toggleAll = () => setSelected(allOn ? [] : students.map((s) => s.id));
  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="px-3 py-2 border-b bg-slate-50/50 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">
          {selected.length} of {students.length} selected
        </span>
        <button
          onClick={toggleAll}
          className="text-xs erp-text-primary font-medium hover:underline"
          data-testid="toggle-all"
        >
          {allOn ? "Clear" : "Select all"}
        </button>
      </div>
      <div className="max-h-72 overflow-auto text-xs divide-y divide-slate-100">
        {students.map((s) => (
          <label
            key={s.id}
            className="flex items-center gap-2 py-1.5 px-3 hover:bg-slate-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.includes(s.id)}
              onChange={() =>
                setSelected((sel) =>
                  sel.includes(s.id)
                    ? sel.filter((x) => x !== s.id)
                    : [...sel, s.id],
                )
              }
              data-testid={`stu-${s.id}`}
            />
            <span className="font-mono text-slate-500">{s.admission_no}</span>
            <span>
              {s.first_name} {s.last_name}
            </span>
            {s.roll_no && (
              <span className="ml-auto text-slate-400">Roll #{s.roll_no}</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Allotment() {
  const rollPermissions = useRouteModulePermissions("allotment", "roll");
  const sectionPermissions = useRouteModulePermissions("allotment", "section");
  const housePermissions = useRouteModulePermissions("allotment", "house");
  const clubPermissions = useRouteModulePermissions("allotment", "club");
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [houses, setHouses] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [students, setStudents] = useState([]);
  const [classId, setClassId] = useState("");
  const [section, setSection] = useState("");

  useEffect(() => {
    (async () => {
      const [c, h, cl] = await Promise.all([
        api.get("/classes"),
        api.get("/houses"),
        api.get("/clubs"),
      ]);
      setClasses(c.data.items || []);
      setHouses(h.data.items || []);
      setClubs(cl.data.items || []);
    })();
  }, []);

  const loadStudents = useCallback(async () => {
    const params = { branch_id: branchId, session_id: sessionId, limit: 1000 };
    if (classId) params.class_id = classId;
    if (section) params.section = section;
    const { data } = await api.get("/students", { params });
    setStudents(data.items || []);
  }, [branchId, sessionId, classId, section]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const cls = classes.find((c) => c.id === classId);
  const sectionsAvailable = cls?.sections || [];
  const visibleTabs = [
    rollPermissions.canView && "roll",
    sectionPermissions.canView && "section",
    housePermissions.canView && "house",
    clubPermissions.canView && "club",
  ].filter(Boolean);

  if (visibleTabs.length === 0) {
    return (
      <div
        data-testid="allotment-page"
        className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500"
      >
        No allotment workflow assigned for this role.
      </div>
    );
  }

  return (
    <div data-testid="allotment-page">
      <PageTitle
        title="Bulk Allotment"
        subtitle="Roll No · Section · House · Club · Category — assign in one click"
      />

      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 shadow-sm">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Class
            </Label>
            <select
              className="h-9 rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={classId}
              onChange={(e) => {
                setClassId(e.target.value);
                setSection("");
              }}
              data-testid="al-class"
            >
              <option value="">All</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Section
            </Label>
            <select
              className="h-9 rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              data-testid="al-section"
              disabled={!classId}
            >
              <option value="">All</option>
              {sectionsAvailable.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Tabs defaultValue={visibleTabs[0]}>
        <TabsList>
          {rollPermissions.canView && (
            <TabsTrigger value="roll" data-testid="al-tab-roll">
              <Hash className="w-3.5 h-3.5 mr-1.5" /> Roll No
            </TabsTrigger>
          )}
          {sectionPermissions.canView && (
            <TabsTrigger value="section" data-testid="al-tab-section">
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> Section
            </TabsTrigger>
          )}
          {housePermissions.canView && (
            <TabsTrigger value="house" data-testid="al-tab-house">
              <Trophy className="w-3.5 h-3.5 mr-1.5" /> House
            </TabsTrigger>
          )}
          {clubPermissions.canView && (
            <TabsTrigger value="club" data-testid="al-tab-club">
              <Award className="w-3.5 h-3.5 mr-1.5" /> Club
            </TabsTrigger>
          )}
        </TabsList>

        {rollPermissions.canView && (
          <TabsContent value="roll" className="mt-3">
            <RollNoPanel
              classId={classId}
              section={section}
              branchId={branchId}
              sessionId={sessionId}
              reload={loadStudents}
              disabled={!rollPermissions.canAdd && !rollPermissions.canEdit}
            />
          </TabsContent>
        )}
        {sectionPermissions.canView && (
          <TabsContent value="section" className="mt-3">
            <BulkAssignPanel
              field="section"
              label="Section"
              options={sectionsAvailable.map((s) => ({ id: s, name: s }))}
              students={students}
              reload={loadStudents}
              disabled={
                !sectionPermissions.canAdd && !sectionPermissions.canEdit
              }
            />
          </TabsContent>
        )}
        {housePermissions.canView && (
          <TabsContent value="house" className="mt-3">
            <BulkAssignPanel
              field="house_id"
              label="House"
              options={houses}
              students={students}
              reload={loadStudents}
              disabled={!housePermissions.canAdd && !housePermissions.canEdit}
            />
          </TabsContent>
        )}
        {clubPermissions.canView && (
          <TabsContent value="club" className="mt-3">
            <BulkAssignPanel
              field="club_id"
              label="Club"
              options={clubs}
              students={students}
              reload={loadStudents}
              disabled={!clubPermissions.canAdd && !clubPermissions.canEdit}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function RollNoPanel({
  classId,
  section,
  branchId,
  sessionId,
  reload,
  disabled,
}) {
  const [sortBy, setSortBy] = useState("name");
  const run = async () => {
    if (disabled) {
      toast.error("You do not have permission for roll number allotment");
      return;
    }
    if (!classId || !section) {
      toast.error("Pick class and section first");
      return;
    }
    const { data } = await api.post("/allotment/roll-no", {
      class_id: classId,
      section,
      branch_id: branchId,
      session_id: sessionId,
      sort_by: sortBy,
    });
    toast.success(`${data.updated} roll numbers assigned`);
    reload();
  };
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4 shadow-sm">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Sort by
          </Label>
          <select
            className="h-9 rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="sort-by"
          >
            <option value="name">Name (A → Z)</option>
            <option value="dob">Date of Birth</option>
            <option value="admission">Admission No</option>
          </select>
        </div>
        <Button
          onClick={run}
          className="h-9 erp-primary"
          data-testid="run-rollno"
          disabled={disabled}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate Roll Numbers
        </Button>
      </div>
      <div className="mt-3 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
        Pick a class + section above. Existing roll numbers will be overwritten
        in sequence (1, 2, 3 …).
      </div>
    </div>
  );
}

function BulkAssignPanel({
  field,
  label,
  options,
  students,
  reload,
  disabled,
}) {
  const [selected, setSelected] = useState([]);
  const [value, setValue] = useState("");
  const run = async () => {
    if (disabled) {
      toast.error(`You do not have permission to assign ${label}`);
      return;
    }
    if (selected.length === 0) {
      toast.error("Select students first");
      return;
    }
    if (!value) {
      toast.error(`Choose a ${label}`);
      return;
    }
    const { data } = await api.post("/allotment/bulk-assign", {
      field,
      value,
      student_ids: selected,
    });
    toast.success(`${data.updated} students assigned to ${label}`);
    setSelected([]);
    reload();
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <StudentPicker
        students={students}
        selected={selected}
        setSelected={setSelected}
      />
      <div className="border border-slate-200 bg-white rounded-md p-4 shadow-sm h-fit">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">
          Assign to {label}
        </h3>
        <Label className="text-xs font-semibold text-slate-700 mb-1 block">
          {label}
        </Label>
        <select
          className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`pick-${field}`}
        >
          <option value="">— Choose —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <Button
          onClick={run}
          className="mt-3 erp-primary w-full"
          data-testid={`run-${field}`}
          disabled={disabled}
        >
          Assign {selected.length} student(s)
        </Button>
      </div>
    </div>
  );
}
